import type {
  Partner,
  Purchase,
  PurchaseHistoryEntry,
  PurchasePhase,
} from "@tsi/domain";

// Pinned "today" para gerar datas relativas estáveis na POC.
const TODAY = new Date("2026-05-09T18:00:00Z");

function hoursAgo(h: number): string {
  const dt = new Date(TODAY);
  dt.setHours(dt.getHours() - h);
  return dt.toISOString();
}
function daysAgo(d: number): string {
  return hoursAgo(d * 24).slice(0, 10);
}
function daysFromNow(d: number): string {
  const dt = new Date(TODAY);
  dt.setDate(dt.getDate() + d);
  return dt.toISOString().slice(0, 10);
}

// Parceiros recorrentes — usados para demonstrar o filtro "Parceiro"
const CONEX: Partner = { name: "Conex Consórcio", commissionPct: 0.03 };
const TORTATO: Partner = { name: "Tortato Corporações", commissionPct: 0.025 };
const SERVOPA: Partner = { name: "Servopa Private", commissionPct: 0.03 };

// Helpers para construir histórico em ordem cronológica
function h(
  hoursAgoFromNow: number,
  type: PurchaseHistoryEntry["type"],
  message: string,
  author?: string,
): PurchaseHistoryEntry {
  return { at: hoursAgo(hoursAgoFromNow), type, message, author };
}

export const mockPurchases: Purchase[] = [
  // ───────────────────────── COLUNA 0: NOVO ────────────────────────────
  {
    id: "pu-new1",
    cardRef: 8270,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Cláudio Werner",
      cpf: "100.200.300-44",
      phone: "(41) 99500-1122",
      contemplatedValue: 78_400,
    },
    phase: "new",
    phaseEnteredAt: hoursAgo(2),
    offerDate: hoursAgo(2).slice(0, 10),
    creditStatus: "pending",
    capturedBy: "Gabriel",
    channel: "whatsapp",
    history: [
      h(2, "lead_received", "Lead recebido via WhatsApp — aguardando precificação", "Gabriel"),
    ],
  },
  {
    id: "pu-new2",
    cardRef: 8271,
    assetType: "auto",
    contemplatedClient: {
      name: "Marina Schultz",
      cpf: "110.220.330-55",
      phone: "(41) 98800-2233",
      contemplatedValue: 41_200,
    },
    phase: "new",
    phaseEnteredAt: hoursAgo(6),
    offerDate: hoursAgo(6).slice(0, 10),
    creditStatus: "pending",
    capturedBy: "Isaac",
    channel: "phone",
    partner: TORTATO,
    history: [
      h(6, "lead_received", "Cliente ligou pedindo proposta de compra", "Isaac"),
    ],
  },
  {
    id: "pu-new3",
    cardRef: 8272,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Bernardes Empreendimentos S/A",
      cpf: "12.023.034/0001-66",
      phone: "(41) 99977-8899",
      contemplatedValue: 132_800,
    },
    phase: "new",
    phaseEnteredAt: hoursAgo(14), // > 8h → laranja
    offerDate: hoursAgo(14).slice(0, 10),
    creditStatus: "pending",
    capturedBy: "Amélia",
    channel: "email",
    partner: CONEX,
    history: [
      h(14, "lead_received", "Cliente indicado pela Conex Consórcio", "Amélia"),
    ],
  },
  {
    id: "pu-new4",
    cardRef: 8273,
    assetType: "auto",
    contemplatedClient: {
      name: "Renata Quirino",
      cpf: "130.240.350-77",
      phone: "(41) 99112-6677",
      contemplatedValue: 56_500,
    },
    phase: "new",
    phaseEnteredAt: hoursAgo(28), // > 24h → vermelho
    offerDate: hoursAgo(28).slice(0, 10),
    creditStatus: "pending",
    capturedBy: "Gabriel",
    channel: "partner",
    history: [
      h(28, "lead_received", "Cliente entrou em contato pelo site", "Gabriel"),
    ],
  },

  // ───────────────────── COLUNA 1: OFERTA ENVIADA ─────────────────────
  {
    id: "pu1",
    cardRef: 7299,
    assetType: "real_estate",
    contemplatedClient: {
      name: "José Santos",
      cpf: "123.456.789-00",
      phone: "(41) 99812-4421",
      contemplatedValue: 56_224,
    },
    offer: 47_500,
    margin: 0.18,
    phase: "offer",
    phaseEnteredAt: hoursAgo(8),
    offerDate: hoursAgo(8).slice(0, 10),
    creditStatus: "pending",
    history: [
      h(8, "offer_sent", "Oferta de R$ 47.500 enviada via WhatsApp", "Gabriel"),
    ],
  },
  {
    id: "pu2",
    cardRef: 7250,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Marcia Ferreira",
      cpf: "234.567.890-11",
      phone: "(41) 98712-9988",
      contemplatedValue: 58_157,
    },
    offer: 49_000,
    margin: 0.16,
    phase: "offer",
    phaseEnteredAt: hoursAgo(36),
    offerDate: daysAgo(2),
    creditStatus: "pending",
    partner: CONEX,
    history: [
      h(36, "offer_sent", "Oferta de R$ 49.000 enviada", "Isaac"),
    ],
  },
  {
    id: "pu3",
    cardRef: 7359,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Roberto Lima",
      cpf: "345.678.901-22",
      phone: "(41) 99432-1190",
      contemplatedValue: 64_398,
    },
    offer: 53_000,
    margin: 0.20,
    phase: "offer",
    phaseEnteredAt: hoursAgo(54), // > 48h → atenção
    offerDate: daysAgo(2),
    creditStatus: "pending",
    history: [
      h(54, "offer_sent", "Oferta de R$ 53.000 enviada via WhatsApp", "Amélia"),
    ],
  },
  {
    id: "pu4",
    cardRef: 8132,
    assetType: "auto",
    contemplatedClient: {
      name: "Diego Almeida",
      cpf: "456.789.012-33",
      phone: "(41) 99001-2255",
      contemplatedValue: 42_700,
    },
    offer: 35_000,
    margin: 0.14,
    phase: "offer",
    phaseEnteredAt: hoursAgo(80), // > 72h → vermelho
    offerDate: daysAgo(4),
    creditStatus: "pending",
    partner: TORTATO,
    history: [
      h(80, "offer_sent", "Oferta de R$ 35.000 enviada", "Gabriel"),
    ],
  },
  {
    id: "pu5",
    cardRef: 7341,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Letícia Cardoso",
      cpf: "567.890.123-44",
      phone: "(41) 99654-2341",
      contemplatedValue: 56_489,
    },
    offer: 47_800,
    margin: 0.17,
    phase: "offer",
    phaseEnteredAt: hoursAgo(20),
    offerDate: hoursAgo(20).slice(0, 10),
    creditStatus: "pending",
    history: [h(20, "offer_sent", "Oferta inicial enviada", "Isaac")],
  },
  {
    id: "pu6",
    cardRef: 8147,
    assetType: "auto",
    contemplatedClient: {
      name: "Paulo Henrique",
      cpf: "678.901.234-55",
      phone: "(41) 99887-7766",
      contemplatedValue: 45_200,
    },
    offer: 37_500,
    margin: 0.15,
    phase: "offer",
    phaseEnteredAt: hoursAgo(60), // > 48h → laranja
    offerDate: daysAgo(3),
    creditStatus: "pending",
    history: [h(60, "offer_sent", "Oferta enviada via WhatsApp", "Amélia")],
  },

  // ─────────────────────── COLUNA 2: CONTRAPROPOSTA ───────────────────────
  {
    id: "pu7",
    cardRef: 7297,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Adriana Pinto",
      cpf: "789.012.345-66",
      phone: "(41) 98321-4477",
      contemplatedValue: 58_663,
    },
    offer: 49_300,
    counterOffer: { value: 51_500, date: daysAgo(1) },
    margin: 0.13,
    phase: "counter_offer",
    phaseEnteredAt: hoursAgo(18),
    offerDate: daysAgo(4),
    creditStatus: "pending",
    partner: SERVOPA,
    history: [
      h(96, "offer_sent", "Oferta de R$ 49.300 enviada", "Gabriel"),
      h(
        18,
        "counter_received",
        "Cliente contraofertou R$ 51.500 (quer +R$ 2.200)",
        "Cliente",
      ),
    ],
  },
  {
    id: "pu8",
    cardRef: 7276,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Fábio Cunha",
      cpf: "890.123.456-77",
      phone: "(41) 99119-2244",
      contemplatedValue: 58_781,
    },
    offer: 49_400,
    counterOffer: { value: 52_000, date: daysAgo(1) },
    margin: 0.10,
    phase: "counter_offer",
    phaseEnteredAt: hoursAgo(28), // > 24h
    offerDate: daysAgo(3),
    creditStatus: "pending",
    history: [
      h(72, "offer_sent", "Oferta de R$ 49.400 enviada", "Isaac"),
      h(28, "counter_received", "Cliente quer R$ 52.000", "Cliente"),
    ],
  },
  {
    id: "pu9",
    cardRef: 8156,
    assetType: "auto",
    contemplatedClient: {
      name: "Henrique Carvalho",
      cpf: "901.234.567-88",
      phone: "(41) 98765-4321",
      contemplatedValue: 48_800,
    },
    offer: 40_500,
    counterOffer: { value: 42_800, date: daysAgo(2) },
    margin: 0.09,
    phase: "counter_offer",
    phaseEnteredAt: hoursAgo(50), // > 48h → vermelho
    offerDate: daysAgo(6),
    creditStatus: "pending",
    partner: TORTATO,
    history: [
      h(144, "offer_sent", "Oferta enviada", "Amélia"),
      h(
        50,
        "counter_received",
        "Cliente contraofertou R$ 42.800 — margem fica em 9%",
        "Cliente",
      ),
    ],
  },
  {
    id: "pu10",
    cardRef: 7273,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Sandra Ribeiro",
      cpf: "012.345.678-99",
      phone: "(41) 99876-1234",
      contemplatedValue: 66_145,
    },
    offer: 55_500,
    counterOffer: { value: 57_000, date: hoursAgo(6).slice(0, 10) },
    margin: 0.14,
    phase: "counter_offer",
    phaseEnteredAt: hoursAgo(6),
    offerDate: daysAgo(2),
    creditStatus: "pending",
    history: [
      h(48, "offer_sent", "Oferta de R$ 55.500 enviada", "Gabriel"),
      h(6, "counter_received", "Cliente quer R$ 57.000", "Cliente"),
    ],
  },

  // ─────────────────────── COLUNA 3: APROVADO ────────────────────────────
  {
    id: "pu11",
    cardRef: 7258,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Felipe Moraes",
      cpf: "111.222.333-44",
      phone: "(41) 99334-5566",
      contemplatedValue: 65_301,
    },
    offer: 54_900,
    margin: 0.17,
    phase: "approved",
    phaseEnteredAt: daysAgo(2),
    offerDate: daysAgo(8),
    creditStatus: "approved",
    contractStatus: "sent",
    contractSentAt: daysAgo(2),
    deadline: daysFromNow(5),
    partner: CONEX,
    history: [
      h(8 * 24, "offer_sent", "Oferta de R$ 54.900 enviada", "Gabriel"),
      h(6 * 24, "counter_received", "Cliente aceitou", "Cliente"),
      h(5 * 24, "approved", "Eustac confirmou margem viável", "Eustac"),
      h(2 * 24, "contract_sent", "Contrato enviado via DocuSign", "Andressa"),
    ],
  },
  {
    id: "pu12",
    cardRef: 7263,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Larissa Tavares",
      cpf: "222.333.444-55",
      phone: "(41) 98221-3344",
      contemplatedValue: 65_462,
    },
    offer: 55_000,
    margin: 0.13,
    phase: "approved",
    phaseEnteredAt: daysAgo(6),
    offerDate: daysAgo(10),
    creditStatus: "approved",
    contractStatus: "signed",
    contractSentAt: daysAgo(6),
    deadline: daysFromNow(1),
    history: [
      h(10 * 24, "offer_sent", "Oferta enviada", "Isaac"),
      h(8 * 24, "approved", "Cliente aceitou diretamente", "Cliente"),
      h(6 * 24, "contract_sent", "Contrato enviado", "Andressa"),
      h(2 * 24, "contract_signed", "Cliente assinou DocuSign", "Cliente"),
    ],
  },
  {
    id: "pu13",
    cardRef: 7316,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Vinícius Brandão",
      cpf: "333.444.555-66",
      phone: "(41) 99655-4477",
      contemplatedValue: 65_597,
    },
    offer: 55_200,
    margin: 0.15,
    phase: "approved",
    phaseEnteredAt: daysAgo(8), // > 7 dias → alerta
    offerDate: daysAgo(15),
    creditStatus: "approved",
    contractStatus: "sent",
    contractSentAt: daysAgo(8),
    deadline: daysAgo(1),
    history: [
      h(15 * 24, "offer_sent", "Oferta enviada", "Amélia"),
      h(10 * 24, "approved", "Cliente aprovou após contraproposta", "Cliente"),
      h(8 * 24, "contract_sent", "Contrato enviado", "Andressa"),
    ],
  },
  {
    id: "pu14",
    cardRef: 8163,
    assetType: "auto",
    contemplatedClient: {
      name: "Camila Rosa",
      cpf: "444.555.666-77",
      phone: "(41) 99001-7766",
      contemplatedValue: 52_400,
    },
    offer: 43_800,
    margin: 0.16,
    phase: "approved",
    phaseEnteredAt: daysAgo(3),
    offerDate: daysAgo(7),
    creditStatus: "approved",
    contractStatus: "paid",
    contractSentAt: daysAgo(5),
    deadline: daysFromNow(3),
    partner: TORTATO,
    history: [
      h(7 * 24, "offer_sent", "Oferta enviada", "Gabriel"),
      h(5 * 24, "contract_sent", "Contrato DocuSign enviado", "Andressa"),
      h(3 * 24, "contract_signed", "Cliente assinou", "Cliente"),
      h(36, "paid", "Pagamento R$ 43.800 efetuado", "Lucca"),
    ],
  },

  // ─────────────────────── COLUNA 4: EM ESTOQUE ──────────────────────────
  {
    id: "pu15",
    cardRef: 7227,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Vanessa Almeida",
      cpf: "555.666.777-88",
      phone: "(41) 98234-9912",
      contemplatedValue: 67_224,
    },
    offer: 56_200,
    margin: 0.12,
    phase: "stock",
    phaseEnteredAt: daysAgo(5),
    enteredStockAt: daysAgo(5),
    offerDate: daysAgo(25),
    creditStatus: "approved",
    contractStatus: "paid",
    updatedCredit: 68_410,
    partner: CONEX,
    history: [
      h(25 * 24, "offer_sent", "Oferta enviada", "Gabriel"),
      h(20 * 24, "approved", "Cliente aceitou", "Cliente"),
      h(15 * 24, "contract_signed", "Contrato assinado", "Cliente"),
      h(10 * 24, "paid", "Pagamento efetuado", "Lucca"),
      h(5 * 24, "stock_in", "Cota registrada no Siscom", "Sistema"),
    ],
  },
  {
    id: "pu16",
    cardRef: 7235,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Rafael Bittencourt",
      cpf: "666.777.888-99",
      phone: "(41) 99112-3344",
      contemplatedValue: 111_218,
    },
    offer: 92_500,
    margin: 0.18,
    phase: "stock",
    phaseEnteredAt: daysAgo(12),
    enteredStockAt: daysAgo(12),
    offerDate: daysAgo(35),
    creditStatus: "approved",
    contractStatus: "paid",
    updatedCredit: 113_005,
    history: [
      h(35 * 24, "offer_sent", "Oferta enviada", "Isaac"),
      h(30 * 24, "approved", "Cliente aprovou", "Cliente"),
      h(20 * 24, "contract_signed", "Contrato assinado", "Cliente"),
      h(14 * 24, "paid", "Pagamento efetuado", "Lucca"),
      h(12 * 24, "stock_in", "Em estoque", "Sistema"),
    ],
  },
  {
    id: "pu17",
    cardRef: 8189,
    assetType: "auto",
    contemplatedClient: {
      name: "Bianca Oliveira",
      cpf: "777.888.999-00",
      phone: "(41) 98712-3399",
      contemplatedValue: 58_900,
    },
    offer: 49_200,
    margin: 0.14,
    phase: "stock",
    phaseEnteredAt: daysAgo(2),
    enteredStockAt: daysAgo(2),
    offerDate: daysAgo(18),
    creditStatus: "approved",
    contractStatus: "paid",
    updatedCredit: 59_440,
    partner: TORTATO,
    history: [
      h(18 * 24, "offer_sent", "Oferta enviada", "Amélia"),
      h(15 * 24, "counter_received", "Cliente contraofertou", "Cliente"),
      h(13 * 24, "counter_accepted", "Eustac aprovou contraproposta", "Eustac"),
      h(7 * 24, "contract_signed", "Contrato assinado", "Cliente"),
      h(3 * 24, "paid", "Pagamento efetuado", "Lucca"),
      h(2 * 24, "stock_in", "Em estoque", "Sistema"),
    ],
  },
  {
    id: "pu18",
    cardRef: 7172,
    assetType: "real_estate",
    contemplatedClient: {
      name: "Eduardo Pacheco",
      cpf: "888.999.000-11",
      phone: "(41) 99887-1122",
      contemplatedValue: 70_664,
    },
    offer: 59_100,
    margin: 0.16,
    phase: "stock",
    phaseEnteredAt: daysAgo(20),
    enteredStockAt: daysAgo(20),
    offerDate: daysAgo(40),
    creditStatus: "approved",
    contractStatus: "paid",
    updatedCredit: 71_550,
    partner: SERVOPA,
    history: [
      h(40 * 24, "offer_sent", "Oferta enviada", "Gabriel"),
      h(33 * 24, "approved", "Cliente aprovou", "Cliente"),
      h(25 * 24, "contract_signed", "Contrato assinado", "Cliente"),
      h(22 * 24, "paid", "Pagamento efetuado", "Lucca"),
      h(20 * 24, "stock_in", "Em estoque", "Sistema"),
    ],
  },
];

export const PARTNERS_LIST: string[] = Array.from(
  new Set(
    mockPurchases
      .map((p) => p.partner?.name)
      .filter((n): n is string => Boolean(n)),
  ),
).sort();

export const PHASE_BUCKET: Record<
  PurchasePhase,
  "incoming" | "negotiating" | "approved" | "stock"
> = {
  new: "incoming",
  offer: "negotiating",
  counter_offer: "negotiating",
  approved: "approved",
  stock: "stock",
};
